import { useAssetVizInternal } from '../AssetVizInternalProvider'
import {
  BoundsCalculator,
  DefaultCamera,
  FitCalculator,
  PositionCalculator,
  SelectionCallback,
} from '../cameras/DefaultCamera'
import { LegacyConeSurface3D } from '../objects/surfaces/LegacyConeSurface3D'
import { ShellSurface3D } from '../objects/surfaces/ShellSurface3D'
import React from 'react'
import {
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoEditableComponent,
  GeckoHorizontalTankShell,
  GeckoLegacyCone,
  GeckoMeasurementUnit,
} from 'types'

import { SceneFactoryConfig } from './sceneFactory'

export const HorizontalTankSceneConfig: SceneFactoryConfig = {
  name: 'HorizontalTank',
  assetModels: [GeckoAssetModelType.HorizontalTank],
  componentModels: [
    GeckoComponentModelType.HorizontalTankShell,
    GeckoComponentModelType.LegacyCone,
  ],
  sceneFunc: (props) => <HorizontalTankScene {...props} />,
}

/**
 * A scene depicting a cylindrical tank (not a military tank, mind you) which
 * wraps around the x-axis.
 * @param assetModel      The model used to represent the whole asset
 * @param componentModel  The model for the component in question
 * @constructor
 */
export const HorizontalTankScene: React.FC<{
  assetModel: GeckoAssetModel
  componentModel: GeckoEditableComponent
}> = ({ assetModel, componentModel }) => {
  const selectedModel = componentModel as
    | GeckoHorizontalTankShell
    | GeckoLegacyCone

  //----------------------------------------------------------------------------
  const { show3D, componentDataFrames } = useAssetVizInternal()

  const componentBounds = React.useMemo(() => {
    if (selectedModel.type === GeckoComponentModelType.LegacyCone)
      return GeckoComponentRect.CreateFromXYs(
        selectedModel.componentBounds.componentID,
        -selectedModel.componentBounds.end.y.num,
        -selectedModel.componentBounds.end.y.num,
        selectedModel.componentBounds.end.y.num,
        selectedModel.componentBounds.end.y.num,
        selectedModel.componentBounds.y.unit,
        selectedModel.componentBounds.y.unit
      )
    const componentBounds =
      selectedModel.componentBounds ??
      GeckoComponentRect.CreateFromXYs(
        '',
        0,
        0,
        1,
        1,
        GeckoMeasurementUnit.Meters,
        GeckoMeasurementUnit.Meters
      )
    if (
      componentBounds.x.unit !== GeckoMeasurementUnit.Meters ||
      componentBounds.y.unit !== GeckoMeasurementUnit.Meters
    )
      throw new Error(
        `Tank shells in the asset model should always be in meters, but 
              the x range is in ${componentBounds.x.unit}
              and y range is in ${componentBounds.y.unit}`
      )
    return componentBounds
  }, [selectedModel.componentBounds, selectedModel.type])

  const tankRadius = React.useMemo(
    () =>
      selectedModel instanceof GeckoLegacyCone
        ? selectedModel.outerRadius
        : selectedModel.radius,
    [selectedModel]
  )

  const onComponentSelected = React.useCallback<SelectionCallback>(
    () =>
      // This starts the camera off wherever there's data to look at
      selectedModel.type === GeckoComponentModelType.LegacyCone
        ? componentBounds
        : (componentDataFrames
            ?.at(0)
            ?.data?.geometry?.project(componentBounds.space) ??
          componentBounds),
    [selectedModel.type, componentBounds, componentDataFrames]
  )

  const getCameraBounds = React.useCallback<BoundsCalculator>(
    () => ({
      rect: componentBounds,
      doWrapY: show3D,
    }),
    [componentBounds, show3D]
  )

  const getCameraPosition = React.useCallback<PositionCalculator>(
    ({ componentX, componentY, distToComponent }) => {
      if (selectedModel.type === GeckoComponentModelType.LegacyCone)
        return {
          position: [componentX, componentY, distToComponent],
          rotation: [0, 0, 0],
        }
      const relX = componentX - componentBounds.start.x.num
      const relY = componentY - componentBounds.start.y.num
      if (!show3D)
        return {
          position: [relX, relY, distToComponent],
          rotation: [0, 0, 0],
        }
      // ...otherwise we're looking at the shell in 3D mode
      const angle =
        2.0 * Math.PI * (relY / (tankRadius.num * 2 * Math.PI) - 0.25)

      const distToAxis = tankRadius.num + distToComponent
      return {
        position: [
          relX,
          distToAxis * Math.sin(angle),
          distToAxis * Math.cos(angle),
        ],
        rotation: [-angle, 0, 0],
      }
    },
    [show3D, selectedModel, componentBounds, tankRadius]
  )

  const getCameraDimensionsFit = React.useCallback<FitCalculator>(
    (rect) =>
      show3D
        ? [rect.width.num, Math.min(rect.height.num, tankRadius.num * 2.0)]
        : [rect.width.num, rect.height.num],
    [show3D, tankRadius]
  )

  //----------------------------------------------------------------------------

  return !selectedModel ? null : (
    <group>
      <DefaultCamera
        onComponentSelected={onComponentSelected}
        getCameraBounds={getCameraBounds}
        getCameraPosition={getCameraPosition}
        getCameraDimensionsFit={getCameraDimensionsFit}
      />
      {selectedModel instanceof GeckoHorizontalTankShell ? (
        <ShellSurface3D model={selectedModel} assetModel={assetModel} />
      ) : selectedModel instanceof GeckoLegacyCone ? (
        <LegacyConeSurface3D model={selectedModel} assetModel={assetModel} />
      ) : null}
    </group>
  )
}
